import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { ProtectedRoute } from '../components/ProtectedRoute';
import {
  listCompanies,
  getCompanyDetail,
  createCompany,
  updateCompanyName,
  addUserToCompany,
  getBackupStatus,
  type CompanyWithStats,
  type CompanyDetail,
  type BackupStatus,
} from '../lib/superadmin';
import { toast } from 'sonner';

export const Route = createFileRoute('/superadmin')({
  component: SuperadminPage,
});

function SuperadminPage() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <ProtectedRoute allowedRoles={['super_admin']}>
      <SuperadminContent />
    </ProtectedRoute>
  );
}

// ── Main content ──────────────────────────────────────────────────────────────

type View = 'list' | 'new' | { companyId: string };

function SuperadminContent() {
  const { user } = useAuth();

  const [view, setView] = useState<View>('list');
  const [companies, setCompanies] = useState<CompanyWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [backupStatus, setBackupStatus] = useState<BackupStatus | null>(null);

  const fetchCompanies = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listCompanies();
      setCompanies(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
    getBackupStatus().then(setBackupStatus).catch(() => null);
  }, []);

  if (view === 'new') {
    return (
      <NewCompanyForm
        onBack={() => setView('list')}
        onCreated={() => { fetchCompanies(); setView('list'); }}
      />
    );
  }

  if (typeof view === 'object') {
    return (
      <CompanyDetailView
        companyId={view.companyId}
        onBack={() => { fetchCompanies(); setView('list'); }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Pelajari Superadmin</h1>
          <p className="text-sm text-gray-500 mt-0.5">Logged in as {user?.name} ({user?.email})</p>
        </div>
        <button
          onClick={() => setView('new')}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
        >
          + New Company
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent" />
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <p className="text-lg font-medium">No companies yet</p>
            <p className="text-sm mt-1">Create the first company to get started.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Company</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Users</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Created</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {companies.map((company) => (
                  <tr key={company.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="font-medium text-gray-900">{company.name}</div>
                      <div className="text-xs text-gray-400 font-mono mt-0.5">{company.id}</div>
                    </td>
                    <td className="px-5 py-3.5 text-gray-700">{company.userCount} users</td>
                    <td className="px-5 py-3.5 text-gray-500">
                      {new Date(company.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        onClick={() => setView({ companyId: company.id })}
                        className="text-teal-600 hover:text-teal-800 font-medium text-sm"
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Stats row */}
        {companies.length > 0 && (
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="text-2xl font-bold text-gray-900">{companies.length}</div>
              <div className="text-sm text-gray-500 mt-1">Total Companies</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="text-2xl font-bold text-gray-900">
                {companies.reduce((sum, c) => sum + c.userCount, 0)}
              </div>
              <div className="text-sm text-gray-500 mt-1">Total Users</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="text-2xl font-bold text-gray-900">
                {companies.filter((c) => c.userCount > 0).length}
              </div>
              <div className="text-sm text-gray-500 mt-1">Active Companies</div>
            </div>
          </div>
        )}

        {/* Backup status card */}
        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-900">Backup Status</h2>
            <button
              onClick={() => getBackupStatus().then(setBackupStatus).catch(() => null)}
              className="text-xs text-teal-600 hover:text-teal-800 font-medium"
            >
              Refresh
            </button>
          </div>

          {!backupStatus ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 animate-pulse">
              <div className="w-3 h-3 rounded-full bg-gray-200" />
              Loading backup status…
            </div>
          ) : !backupStatus.available ? (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <span className="text-amber-500 mt-0.5">⚠</span>
              <div>
                <p className="text-sm font-medium text-amber-800">Backup scripts not installed</p>
                <p className="text-xs text-amber-600 mt-0.5">{backupStatus.message}</p>
                <p className="text-xs text-amber-600 mt-1">
                  See <code className="bg-amber-100 px-1 rounded">scripts/backup/README.md</code> for installation instructions.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Overall health */}
              {!backupStatus.healthy && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <span className="text-red-500 mt-0.5">✕</span>
                  <p className="text-sm font-medium text-red-800">One or more backups are failing or stale — investigate immediately.</p>
                </div>
              )}

              {/* Per-type rows */}
              {([
                { label: 'PostgreSQL', entry: backupStatus.postgres },
                { label: 'MinIO',      entry: backupStatus.minio },
              ] as const).map(({ label, entry }) => (
                <div key={label} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                  {/* Status indicator */}
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                    !entry           ? 'bg-gray-300' :
                    entry.failed     ? 'bg-red-500' :
                    entry.stale      ? 'bg-amber-400' :
                    entry.success    ? 'bg-green-500' :
                                       'bg-gray-300'
                  }`} />
                  <span className="text-sm font-medium text-gray-700 w-24 shrink-0">{label}</span>
                  {!entry ? (
                    <span className="text-xs text-gray-400">No backup recorded yet</span>
                  ) : (
                    <div className="flex-1 min-w-0">
                      <span className={`text-xs font-medium ${
                        entry.failed  ? 'text-red-600' :
                        entry.stale   ? 'text-amber-600' :
                        'text-green-700'
                      }`}>
                        {entry.failed ? 'FAILED' : entry.stale ? 'STALE' : 'OK'}
                      </span>
                      {entry.timestamp && (
                        <span className="text-xs text-gray-400 ml-2">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      )}
                      {(entry.failed || entry.stale) && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{entry.message}</p>
                      )}
                    </div>
                  )}
                </div>
              ))}

              <p className="text-xs text-gray-400 pt-1">
                Backups run daily at 02:00 (PostgreSQL) and 02:30 (MinIO). Retained for 7 days locally.
                {' '}<span className="text-amber-600 font-medium">TODO: Set up Cloudflare R2 offsite sync.</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── New Company Form ──────────────────────────────────────────────────────────

function NewCompanyForm({
  onBack,
  onCreated,
}: {
  onBack: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    companyName: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<{ companyId: string; adminUserId: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await createCompany(form);
      setCreated(result);
      toast.success('Company created successfully');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to create company';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (created) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl border border-gray-200 p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Company Created</h2>
            <p className="text-sm text-gray-500 mt-1">Starter kit provisioned successfully</p>
          </div>

          <div className="space-y-3 text-sm bg-gray-50 rounded-lg p-4">
            <div>
              <span className="text-gray-500">Company:</span>
              <span className="ml-2 font-medium text-gray-900">{form.companyName}</span>
            </div>
            <div>
              <span className="text-gray-500">Company ID:</span>
              <span className="ml-2 font-mono text-gray-800 text-xs">{created.companyId}</span>
            </div>
            <div>
              <span className="text-gray-500">Admin email:</span>
              <span className="ml-2 font-medium text-gray-900">{form.adminEmail}</span>
            </div>
          </div>

          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            Share the Company ID and admin credentials with the new client. They can log in at
            pelajari.ai/login using the Company ID in "Advanced options".
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={onCreated}
              className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"
            >
              Back to Companies
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-3">
        <button onClick={onBack} className="text-gray-500 hover:text-gray-700">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-xl font-semibold text-gray-900">Create New Company</h1>
      </div>

      <div className="max-w-lg mx-auto px-6 py-8">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <p className="text-sm text-gray-500 mb-6">
            Provisioning includes: 5 org levels, 8 core competencies, and 3 metric definitions
            (Delivery Rate, Sprint Velocity, On-Time Completion Rate).
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input
                type="text"
                value={form.companyName}
                onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
                placeholder="Acme Corp"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <hr className="border-gray-100" />
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">First Admin Account</p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Name</label>
              <input
                type="text"
                value={form.adminName}
                onChange={(e) => setForm((f) => ({ ...f, adminName: e.target.value }))}
                placeholder="Jane Doe"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
              <input
                type="email"
                value={form.adminEmail}
                onChange={(e) => setForm((f) => ({ ...f, adminEmail: e.target.value }))}
                placeholder="admin@acme.com"
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Password</label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  value={form.adminPassword}
                  onChange={(e) => setForm((f) => ({ ...f, adminPassword: e.target.value }))}
                  placeholder="Min 8 characters"
                  required
                  minLength={8}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPw ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onBack}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Company'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Company Detail View ───────────────────────────────────────────────────────

const ROLE_COLORS: Record<string, string> = {
  super_admin: 'bg-purple-100 text-purple-700',
  admin: 'bg-red-100 text-red-700',
  hr: 'bg-amber-100 text-amber-700',
  manager: 'bg-blue-100 text-blue-700',
  employee: 'bg-gray-100 text-gray-600',
};

function CompanyDetailView({
  companyId,
  onBack,
}: {
  companyId: string;
  onBack: () => void;
}) {
  const [detail, setDetail] = useState<CompanyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const data = await getCompanyDetail(companyId);
      setDetail(data);
      setNewName(data.company.name);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDetail(); }, []);

  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingName(true);
    try {
      await updateCompanyName(companyId, newName);
      setEditingName(false);
      toast.success('Company name updated');
      fetchDetail();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSavingName(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Company not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-3">
        <button onClick={onBack} className="text-gray-500 hover:text-gray-700">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          {editingName ? (
            <form onSubmit={handleRename} className="flex items-center gap-2">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm font-semibold"
                autoFocus
              />
              <button type="submit" disabled={savingName} className="text-xs text-teal-600 font-medium">
                {savingName ? 'Saving...' : 'Save'}
              </button>
              <button type="button" onClick={() => setEditingName(false)} className="text-xs text-gray-400">
                Cancel
              </button>
            </form>
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-gray-900">{detail.company.name}</h1>
              <button
                onClick={() => setEditingName(true)}
                className="text-gray-400 hover:text-gray-600"
                title="Rename"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            </div>
          )}
          <p className="text-xs text-gray-400 font-mono mt-0.5">{companyId}</p>
        </div>
        <button
          onClick={() => setShowAddUser(true)}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"
        >
          + Add User
        </button>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="text-2xl font-bold text-gray-900">{detail.users.length}</div>
            <div className="text-sm text-gray-500 mt-1">Total Users</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="text-2xl font-bold text-gray-900">
              {new Date(detail.company.createdAt).toLocaleDateString()}
            </div>
            <div className="text-sm text-gray-500 mt-1">Created</div>
          </div>
        </div>

        {showAddUser && (
          <AddUserForm
            companyId={companyId}
            onClose={() => setShowAddUser(false)}
            onAdded={() => { setShowAddUser(false); fetchDetail(); }}
          />
        )}

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-100 font-medium text-sm text-gray-700">
            Users ({detail.users.length})
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-2.5 font-medium text-gray-500">Name</th>
                <th className="text-left px-5 py-2.5 font-medium text-gray-500">Email</th>
                <th className="text-left px-5 py-2.5 font-medium text-gray-500">Role</th>
                <th className="text-left px-5 py-2.5 font-medium text-gray-500">Status</th>
                <th className="text-left px-5 py-2.5 font-medium text-gray-500">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {detail.users.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3">{u.name}</td>
                  <td className="px-5 py-3 text-gray-500">{u.email}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[u.role] ?? 'bg-gray-100 text-gray-600'}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-500">{new Date(u.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Add User Form ─────────────────────────────────────────────────────────────

function AddUserForm({
  companyId,
  onClose,
  onAdded,
}: {
  companyId: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'employee' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await addUserToCompany(companyId, form);
      toast.success('User added successfully');
      onAdded();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-6 bg-white rounded-xl border border-teal-200 p-5">
      <h3 className="font-medium text-gray-900 mb-4">Add User to Company</h3>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
          <input
            type="text" required value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
          <input
            type="email" required value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
          <input
            type="password" required minLength={8} value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
          <select
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
          >
            <option value="employee">Employee</option>
            <option value="manager">Manager</option>
            <option value="hr">HR</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="col-span-2 flex justify-end gap-3">
          <button type="button" onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
            {loading ? 'Adding...' : 'Add User'}
          </button>
        </div>
      </form>
    </div>
  );
}
